import React, { lazy } from "react";

import { configureStore } from "@reduxjs/toolkit";
import auth0 from "auth0-js";
import Aviator from "aviator";
import JwtDecode from "jwt-decode";
import { equals, mergeRight } from "ramda";

import getBaseUri from "get_base_uri";
import { setTrackJSPrettyUrlMetadata } from "helpers/analytics";
import auth0Config from "helpers/auth0_config";
import { getPreviewBannerUrl, getPreviewLogoUrl } from "helpers/preview";
import { SESSION_EXPIRED_AUTH_MESSAGE } from "helpers/session_expired_message";
import getAccountInfo from "interfaces/account_info";
import { getBrandAndStoreInfo, getBrandInfo } from "interfaces/brand_info";
import getOrderHistory from "interfaces/order_history";
import isLoggedIn from "is_logged_in";
import { updateLoyaltyInfo } from "packages/loyalty/actions/common";
import { getLoyalty, getRewards } from "packages/loyalty/api/common";
import { updateConsumerInfo } from "packages/online_ordering/actions/consumer";
import { configureOloStore } from "packages/online_ordering/reducers/onlineOrdering";
import reducers from "reducers";
import accountReducers from "reducers/account";
import { renderIntoApp } from "renderIntoApp";
import storage from "storage";

import {
  captureAuthParams,
  logOut,
  showSetPasswordModal,
  toggleModal,
} from "./actions/authentication";

const env = process.env.NODE_ENV || "development";

const emptyState = {
  storeInfo: null,
  brandInfo: null,
  consumer: {},
  loyalty: {},
};

const accountEmptyState = {
  brandInfo: {},
  consumer: {},
};

const DefaultRouteTarget = {
  landing() {
    if (equals(env, "development")) {
      const Landing = lazy(() => import("components/Landing"));
      renderIntoApp(<Landing />);
      return;
    }

    this.notFound();
  },

  noAccessRedirect() {
    const baseUri = getBaseUri();

    Aviator.navigate(baseUri);
  },

  account(request) {
    if (!isLoggedIn()) {
      this.noAccessRedirect();
    } else {
      const LoyaltyAccountApp = lazy(
        () => import("./packages/loyalty/components/account/LoyaltyAccountApp")
      );
      renderIntoApp(<LoyaltyAccountApp request={request} />);

      Promise.all([getBrandInfo(), getAccountInfo(), getOrderHistory()]).then(
        ([brandInfo, consumer, orderHistory]) => {
          const initialState = {
            ...accountEmptyState,
            brandInfo,
            consumer: {
              contactInfo: consumer,
              orders: orderHistory,
              cards: [],
            },
          };

          const store = configureStore({
            reducer: accountReducers,
            // @ts-expect-error TODO: fix and validate this type
            preloadedState: initialState,
          });

          renderIntoApp(<LoyaltyAccountApp request={request} store={store} />);
        }
      );
    }
  },

  index(request) {
    const LoyaltyApp = lazy(
      () => import("packages/loyalty/components/LoyaltyApp")
    );
    renderIntoApp(<LoyaltyApp request={request} />);

    getBrandAndStoreInfo(brandInfo => {
      const initialState = mergeRight(emptyState, { brandInfo });
      const store = configureStore({
        reducer: reducers,
        // @ts-expect-error TODO: fix and validate this type
        preloadedState: initialState,
      });

      renderIntoApp(<LoyaltyApp request={request} store={store} />);

      if (storage.getItem("displayModalUserTokenExpired")) {
        storage.removeItem("displayModalUserTokenExpired");
        store.dispatch(toggleModal(true, SESSION_EXPIRED_AUTH_MESSAGE));
      }

      if (isLoggedIn(true)) {
        getLoyalty(loyalty => {
          store.dispatch(updateLoyaltyInfo(loyalty));
        });

        getAccountInfo().then(accountInfo => {
          store.dispatch(updateConsumerInfo(accountInfo));
        });
      } else {
        getRewards(rewards => {
          store.dispatch(updateLoyaltyInfo({ rewards }));
        });
      }
    });
  },

  loadAnalytics() {
    // temporarily commenting out as Segment has been turned off for this source
    // click-tracking will be reevaluated and reenabled as part of HQ-8965
    // if (env === "production" && typeof window.analytics !== "undefined") {
    //   if (typeof window.analytics.load === "function")
    //     window.analytics.load(SEGMENT_API_KEY);
    //   if (typeof window.analytics.page === "function") {
    //     // This will set the property values: https://github.com/segmentio/analytics.js/blob/master/analytics.js#L587
    //     window.analytics.page({ referrer: null, search: null, url: null });
    //   }
    // }
  },

  oloIndex(request) {
    const OloApp = lazy(
      () => import("packages/online_ordering/components/OloApp")
    );
    const store = configureOloStore();

    if (request.queryParams.email_token) {
      const { email_token: emailToken, reset } = request.queryParams;

      store.dispatch(captureAuthParams(emailToken, reset));
      store.dispatch(logOut(false));
      store.dispatch(showSetPasswordModal());
    }

    renderIntoApp(<OloApp store={store} />);

    if (storage.getItem("displayModalUserTokenExpired")) {
      storage.removeItem("displayModalUserTokenExpired");
      store.dispatch(toggleModal(true, SESSION_EXPIRED_AUTH_MESSAGE));
    }
  },

  oloAccount(request) {
    const OLOAccountApp = lazy(
      () =>
        import("./packages/online_ordering/components/account/OloAccountApp")
    );
    renderIntoApp(<OLOAccountApp request={request} />);

    if (!isLoggedIn(true)) {
      this.noAccessRedirect();
    }
  },

  paymentHandler(request) {
    const { params } = request;

    if (params.payment_method_token && window.parent) {
      window.parent.postMessage(
        {
          paymentMethodToken: params.payment_method_token,
          source: "upserve-payments",
        },
        window.location.origin
      );
    } else {
      window.parent.postMessage({
        paymentError: true,
        source: "upserve-payments",
      });
    }
  },

  handleSocialLogin() {
    const { domain, clientID } = auth0Config();

    const webAuth = new auth0.WebAuth({
      domain,
      clientID,
      responseType: "token id_token",
      redirectUri: `${window.location.origin}/s/social_login_success`,
    });

    webAuth.parseHash(
      { hash: window.location.hash },
      (parseErr, authResult) => {
        if (parseErr) {
          console.log(parseErr);
          window.location.href = storage.getItem("currentUrl");
        } else {
          storage.setItem("accessToken", authResult.accessToken);
          storage.setItem("idToken", authResult.idToken);
          storage.setItem("jwtExpiration", JwtDecode(authResult.idToken).exp);

          webAuth.client.userInfo(authResult.accessToken, (userErr, user) => {
            if (user) storage.setItem("user", JSON.stringify(user));
            window.location.href = storage.getItem("currentUrl");
          });
        }
      }
    );
  },

  mobilePay() {
    const MobilePayApp = lazy(
      () => import("packages/mobile_pay/components/MobilePayApp")
    );
    renderIntoApp(<MobilePayApp />);
  },

  giftCard() {
    const DigitalGiftCardApp = lazy(
      () => import("packages/digital_gift_cards/components/DigitalGiftCardApp")
    );
    renderIntoApp(<DigitalGiftCardApp />);
  },

  giftCardPreview(request) {
    const bannerUrl = getPreviewBannerUrl(request);
    const logoUrl = getPreviewLogoUrl(request);

    const DigitalGiftCardPreviewApp = lazy(
      () =>
        import(
          "packages/digital_gift_cards/components/DigitalGiftCardPreviewApp"
        )
    );
    renderIntoApp(
      <DigitalGiftCardPreviewApp bannerUrl={bannerUrl} logoUrl={logoUrl} />
    );
  },

  notFound() {
    const baseUri = getBaseUri();

    Aviator.navigate(baseUri);
  },

  trackPrettyUrl() {
    setTrackJSPrettyUrlMetadata();
  },
};

export default DefaultRouteTarget;
